import {
  type PlayerProfileResponse,
  PurchaseContext,
  type SiteResponse,
  type UserJwtResponse,
  v1PurchaseCreate,
} from '@on3/api/src';
import { AuthForm, type AuthViewType } from '@on3/ui-lib/components/Auth/Forms';
import { Checkbox } from '@on3/ui-lib/components/Checkbox/Checkbox';
import { Link } from '@on3/ui-lib/components/Link';
import { useModal } from '@on3/ui-lib/components/Modal/Provider';
import { Recurly } from '@on3/ui-lib/components/Subscription/Recurly';
import { externalApi, useAuth, useSite } from '@on3/ui-lib/index';
import { SiteKeys } from '@on3/ui-lib/utils/constants';
import {
  getAuthorRefKey,
  getContentRefKey,
  getReferrer,
} from '@on3/ui-lib/utils/tracking';
import { CardForm } from '@public/components/Subscription/CardForm';
import { TeamSelect } from '@public/components/Subscription/CheckoutForm/TeamSelect';
import { postPurchase } from '@public/utils/tracking/purchase';
import { type Checkout } from '@public/utils/tracking/types';
import { type CheckoutPrice, type TokenPayload } from '@recurly/recurly-js';
import clsx from 'clsx';
import { useSearchParams } from 'next/navigation';
import { useCallback, useMemo, useState } from 'react';
import { boolean, object, string } from 'yup';

import styles from './CheckoutForm.module.scss';

const schema = object({
  billingToken: object().required(),
  planCode: string().required(),
  acceptTerms: boolean().oneOf([true], 'You must accept the terms of service'),
  confirmPayment: boolean().oneOf(
    [true],
    'You must confirm the payment details',
  ),
});

enum PurchaseErrorMessage {
  GENERAL = 'An error occurred during checkout. Please check your payment method details and try again. If you continue to experience issues, contact support@on3.com.',
  POST_PURCHASE = 'An error occurred after processing your purchase. You should have access, but please contact support@on3.com if you experience any issues.',
  POST_RECRUITS_PURCHASE = 'An error occurred while finalizing your recruit profile. Please contact support@on3.com if you experience any issues.',
}

interface CheckoutFormProps {
  context: PurchaseContext;
  planCode: string;
  offerSetKey: number;
  athlete?: Partial<PlayerProfileResponse>;
  couponCode?: string;
  siteContext?: SiteResponse;
  setPricing?: (price: CheckoutPrice | null) => void;
  onAuth?: (jwt: UserJwtResponse) => void;
}

export const CheckoutForm = ({
  athlete,
  context,
  planCode,
  offerSetKey,
  couponCode,
  siteContext,
  setPricing,
  onAuth,
}: CheckoutFormProps) => {
  const { user, refreshToken } = useAuth();
  const { showModal } = useModal();
  const { currentSite } = useSite();
  const [selectedSite, setSelectedSite] = useState<SiteResponse | null>(null);

  const site = siteContext || currentSite;

  const isNationalSite = [SiteKeys.On3, SiteKeys.Rivals].includes(site.key);

  // Recruits determinates
  const params = useSearchParams();

  const [activeAuthView, setActiveAuthView] =
    useState<AuthViewType>('register');

  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [values, setValues] = useState({
    billingToken: null,
    planCode,
    acceptTerms: false,
    confirmPayment: false,
  });

  const authTitle = useMemo(() => {
    if (activeAuthView === 'authenticated') {
      return 'Account details';
    }

    if (activeAuthView === 'login') {
      return 'Log in to your account to get started';
    }

    return 'Create an account to get started';
  }, [activeAuthView]);

  const onAuthSwitch = useCallback(
    (view: AuthViewType) => {
      setActiveAuthView(view);
    },
    [setActiveAuthView],
  );

  const handleCheckout = useCallback(
    async ({ token }: { token: TokenPayload }) => {
      setErrors({});

      try {
        await schema.validate(
          { ...values, billingToken: token },
          { abortEarly: false },
        );
      } catch (_e: any) {
        setErrors({
          general: 'You must accept all terms to continue.',
        });

        return;
      }

      // Purchase
      let purchase: Checkout = {
        context,
        planCode: values.planCode,
        purchasedOnSiteKey: currentSite.key,
        siteKey: selectedSite?.key || site.key,
        referral: getReferrer(),
        authorKey: getAuthorRefKey(),
        contentKey: getContentRefKey(),
        offerSetKey,
        sportKey: athlete?.defaultSport?.key || null,
      };

      try {
        const response = await v1PurchaseCreate(externalApi, {
          billingToken: token.id,
          ...purchase,
        });

        const {
          paymentMethodExpiryMonth,
          paymentMethodExpiryYear,
          paymentMethodLastFour,
          ...purchaseResponse
        } = response;

        purchase = { ...purchase, ...purchaseResponse };
      } catch (e: any) {
        setErrors({
          general: PurchaseErrorMessage.GENERAL,
        });

        return;
      }

      // Post-purchase
      try {
        await postPurchase.default(user!, purchase);

        if (context === PurchaseContext.Recruits) {
          const data = {
            key: params.get('key'),
            nonce: params.get('nonce'),
            athlete,
          } as Record<string, any>;

          try {
            await postPurchase.recruits(purchase, data);
          } catch (error) {
            setErrors({
              general: PurchaseErrorMessage.POST_RECRUITS_PURCHASE,
            });

            return;
          }
        }

        await refreshToken();

        showModal(
          'user.join.success',
          { context, siteContext: selectedSite || siteContext, purchase },
          'lg',
          true,
        );
      } catch (error) {
        setErrors({
          general: PurchaseErrorMessage.POST_PURCHASE,
        });
      }
    },
    [
      athlete,
      context,
      currentSite.key,
      offerSetKey,
      params,
      refreshToken,
      selectedSite,
      showModal,
      site.key,
      siteContext,
      user,
      values,
    ],
  );

  return (
    <Recurly>
      <div className={styles.block}>
        {isNationalSite && (
          <TeamSelect
            className={styles.step}
            selectedSite={selectedSite}
            setSelectedSite={setSelectedSite}
          />
        )}
        <section className={styles.step}>
          <header>
            <h2>{authTitle}</h2>
          </header>
          <AuthForm
            callback={onAuth}
            defaultView="register"
            onSwitch={onAuthSwitch}
            showHeader={false}
          />
        </section>
        <section
          className={clsx(styles.step, {
            [styles.disabled]: !user?.uid,
          })}
        >
          <header>
            <h2>Payment details</h2>
          </header>
          <CardForm
            callback={handleCheckout}
            disabled={!values.acceptTerms || !values.confirmPayment}
            planCode={planCode}
            setPricing={setPricing}
            submitText="Pay Now"
          >
            <div className={styles.terms}>
              <p className={styles.disclaimer}>
                By providing your card information, you allow On3 Media, Inc to
                charge your card for future payments in accordance with their
                terms.
              </p>
              <div className={styles.actions}>
                <Checkbox
                  error={errors.acceptTerms}
                  id="acceptTerms"
                  onChange={(v) => setValues({ ...values, acceptTerms: v })}
                  value={values.acceptTerms}
                >
                  By clicking “Pay Now”, you acknowledge that you have read and
                  agree to On3 Media&apos;s{' '}
                  <Link href="/page/terms-of-service/" target="_blank">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link href="/page/privacy-policy/" target="_blank">
                    Privacy Policy
                  </Link>
                  .
                </Checkbox>
                <Checkbox
                  error={errors.confirmPayment}
                  id="confirmPayment"
                  label="By clicking “Pay Now”, you authorize On3 Media, Inc. to charge your card today with the amount shown plus tax, and again to automatically renew at then current full price, plus tax. You can cancel your subscription any time before the next renewal date in the settings page of your account. All fees and charges are non-refundable. You will be notified via email of any price increases."
                  onChange={(v) => setValues({ ...values, confirmPayment: v })}
                  value={values.confirmPayment}
                />
              </div>
              {errors.general && (
                <p className={styles.error}>{errors.general}</p>
              )}
            </div>
          </CardForm>
        </section>
      </div>
    </Recurly>
  );
};
